import SVGText from '../svg-text';

const SVG_NS = 'http://www.w3.org/2000/svg';

/**
 * Factory pro vytváření SVG textarea elementů (g > circle + foreignObject + textarea)
 * Separuje logiku vytváření DOM od event handlerů
 */
export default class SVGTextareaFactory {
  /**
   * Vytvoří SVG textarea strukturu (group element s circle a foreignObject)
   * @param {Object} config - konfigurace textarea
   * @param {string} config.id - ID textarea elementu
   * @param {Object} config.position - pozice {x, y}
   * @param {Object} config.dataSvgResolution - rozlišení SVG {width, height}
   * @param {Array} config.dataSvgTextOrientation - orientace ['left'/'right', 'top'/'bottom']
   * @param {string} config.foWidth - šířka foreignObject (např. '15.2rem')
   * @param {string} config.foHeight - výška foreignObject (např. '55px')
   * @param {number} config.textareaMaxlength - maximální délka textu
   * @param {string} config.value - text v textarea
   * @param {boolean} config.clone - je to klon? (default: false)
   * @returns {Object} {group, foreignObject, textarea, circle}
   */
  static create({
    id,
    position = { x: 0, y: 0 },
    dataSvgResolution = null,
    dataSvgTextOrientation = null,
    foWidth = '15.2rem',
    foHeight = '55px',
    textareaMaxlength = null,
    value = '',
    clone = false,
  }) {
    // 0. Vytvoř group element jako wrapper
    const group = document.createElementNS(SVG_NS, 'g');
    group.classList.add('svg-textarea-group');
    if (id) group.id = `${id}-group`;

    // 1. Vytvoř positioning circle marker (první v pořadí pro lepší z-index)
    const circle = document.createElementNS(SVG_NS, 'circle');
    circle.id = id ? `${id}-circle` : '';
    circle.setAttribute('cx', position.x);
    circle.setAttribute('cy', position.y);
    circle.classList.add(
      'svg-circle',
      'svg-circle-text',
      'do-not-remove',
      'unselectable',
    );

    if (clone) {
      circle.setAttribute('data-svg-clone', 'true');
    }

    // 2. Vytvoř foreignObject wrapper
    const foreignObject = document.createElementNS(SVG_NS, 'foreignObject');
    foreignObject.classList.add('svg-text', 'unselectable');
    foreignObject.setAttribute('data-svg-text', '');
    foreignObject.setAttribute('x', position.x || 0);
    foreignObject.setAttribute('y', position.y || 0);

    // Orientační třídy
    if (dataSvgTextOrientation && dataSvgTextOrientation.length >= 2) {
      foreignObject.classList.add(
        `orientation-${dataSvgTextOrientation[0]}`,
        `orientation-${dataSvgTextOrientation[1]}`,
      );
      foreignObject.setAttribute(
        'data-svg-text-orientation',
        dataSvgTextOrientation.join(', '),
      );
    }

    // SVG resolution
    if (
      dataSvgResolution &&
      (dataSvgResolution.width || dataSvgResolution.height)
    ) {
      foreignObject.setAttribute(
        'data-svg-resolution',
        `${dataSvgResolution.width || 0}, ${dataSvgResolution.height || 0}`,
      );
    }

    // Vypočítej transform na základě orientace
    const transform = SVGText.computeTransform(
      foreignObject.classList,
      foWidth,
      foHeight,
    );
    let styleStr = `width: ${foWidth}; height: ${foHeight};`;
    if (transform) {
      styleStr += ` transform: ${transform};`;
    }
    foreignObject.setAttribute('style', styleStr);

    // 2. Vytvoř textarea element
    const textarea = document.createElement('textarea');
    if (id) textarea.id = id;
    textarea.className = 'svg-textarea';
    if (textareaMaxlength) {
      textarea.setAttribute('maxlength', textareaMaxlength);
    }
    textarea.setAttribute('style', `width: ${foWidth}; height: ${foHeight};`);
    textarea.value = value || '';

    if (clone) {
      textarea.setAttribute('data-svg-clone', 'true');
    }

    foreignObject.appendChild(textarea);

    // 4. Sestav hierarchii: group > foreignObject+ circle
    group.appendChild(foreignObject);
    group.appendChild(circle);

    return { group, foreignObject, textarea, circle };
  }
}
